'use client'

import { useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useSession } from 'next-auth/react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { apiClient, apiStreamPost } from '@/lib/api-client'
import { askWithStream, mergeStep } from '@/lib/ava-stream'
import type { StreamToolCall } from '@/lib/sse'
import { useAI } from '@/context/AIContext'
import { useAssistantPersona } from '@/context/AssistantPersonaContext'
import { useBriefing } from '@/context/BriefingContext'
import { CoworkerAvatar } from '@/components/shell/CoworkerAvatar'
import { breadcrumbsFor } from '@/lib/navigation'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { CapabilityBubbles } from './CapabilityBubbles'

// ============================================================================
// Animation Variants
// ============================================================================

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300,
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.15,
    }
  },
}

// ============================================================================
// AI Manager Component
// ============================================================================

export function AIManager() {
  const { data: session } = useSession()
  const {
    isManagerOpen,
    closeManager,
    chatHistory,
    addMessage,
    updateMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    currentPageContext,
    currentSection,
  } = useAI()
  // Plan 03 CHG-05: the panel is the AI Employee's, named from the persona Setting.
  const persona = useAssistantPersona()

  const { briefing } = useBriefing()
  const reduceMotion = useReducedMotion()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  // The stream in flight (plan 03 D18): closing the panel, Escape or a new
  // question aborts it, with no error and no further network traffic.
  const abortRef = useRef<AbortController | null>(null)
  const isStreaming = chatHistory.some((m) => m.isStreaming)

  // Auto-scroll: instant while text streams in (smooth scrolling would lag behind
  // every fragment), smooth once it settles, never animated under reduced motion.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: isStreaming || reduceMotion ? 'auto' : 'smooth' })
  }, [chatHistory, isTyping, isStreaming, reduceMotion])

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isManagerOpen) {
        closeManager()
      }
    }
    
    if (isManagerOpen) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isManagerOpen, closeManager])

  // A closed panel (Escape, the close button, the backdrop, Cmd J) stops the answer.
  useEffect(() => {
    if (!isManagerOpen) abortRef.current?.abort()
  }, [isManagerOpen])
  useEffect(() => () => abortRef.current?.abort(), [])

  // Focus trap - focus the modal when it opens
  useEffect(() => {
    if (isManagerOpen && modalRef.current) {
      modalRef.current.focus()
    }
  }, [isManagerOpen])

  // Ask, streaming first (plan 03 D18). A Retry re-asks the same question into a
  // fresh bubble; the broken one keeps its partial text and its "lost the thread" note.
  const handleSendMessage = useCallback(async (content: string) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    const history = chatHistory
      .filter((m) => !m.isLoading && !m.streamBroken && m.content.trim())
      .map((m) => ({ role: m.role, content: m.content }))
    const body = { message: content, history, context: { page: currentPageContext, section: currentSection } }

    addMessage({ role: 'user', content })
    const answerId = addMessage({ role: 'assistant', content: '', isLoading: true })
    setIsTyping(true)

    try {
      const result = await askWithStream({
        signal: controller.signal,
        stream: (handlers, signal) => apiStreamPost('/api/ai/chat/stream', body, handlers, { signal }),
        fallback: async (signal) => {
          const data = await apiClient.post<{ response: string; tool_calls?: StreamToolCall[] }>(
            '/api/ai/chat',
            body,
            { signal }
          )
          return { text: data.response || '', tools: data.tool_calls }
        },
        onText: (soFar) => updateMessage(answerId, () => ({ content: soFar, isLoading: false, isStreaming: true })),
        onStep: (step) => updateMessage(answerId, (previous) => ({ steps: mergeStep(previous.steps, step) })),
      })

      if (result.kind === 'answer') {
        updateMessage(answerId, () => ({
          content: result.text || "I couldn't put an answer together. Please try again.",
          isLoading: false,
          isStreaming: false,
          toolCalls: result.tools.length > 0 ? result.tools : undefined,
        }))
      } else if (result.kind === 'broken') {
        updateMessage(answerId, () => ({
          content: result.text,
          isLoading: false,
          isStreaming: false,
          streamBroken: true,
          retryOf: content,
        }))
      } else {
        // Aborted on purpose: keep any partial text as it stands, quietly.
        updateMessage(answerId, () => ({ isLoading: false, isStreaming: false }))
      }
    } catch {
      updateMessage(answerId, () => ({
        content: "I couldn't reach that just now. Please try again.",
        isLoading: false,
        isStreaming: false,
      }))
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
        setIsTyping(false)
      }
    }
  }, [addMessage, chatHistory, currentPageContext, currentSection, setIsTyping, updateMessage])

  // Handle quick action click
  const handleQuickAction = (action: string) => {
    handleSendMessage(action)
  }

  // Get page name from context
  const getPageName = () => {
    // Same labels as the sidebar and breadcrumb (plan 03 CHG-18).
    const crumbs = breadcrumbsFor(currentPageContext)
    return crumbs.length === 1 ? crumbs[0].label : crumbs.slice(1).map((crumb) => crumb.label).join(' > ')
  }

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeManager()
    }
  }

  // Only render in browser (for portal)
  if (typeof window === 'undefined') return null

  return createPortal(
    <AnimatePresence mode="wait">
      {isManagerOpen && (
        <>
          {/* Backdrop with blur - covers entire viewport including sidebar */}
          <motion.div
            key="ai-manager-backdrop"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 z-modal-backdrop flex items-center justify-center bg-primary/50 p-4 sm:p-8 md:p-12"
            style={{
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            aria-hidden="true"
          >
            {/* Modal Container - centered and properly sized */}
            <motion.div
              ref={modalRef}
              key="ai-manager-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ai-manager-title"
              aria-describedby="ai-manager-description"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                // Z-index for proper stacking
                'z-modal',
                // Size and positioning
                'relative w-full max-w-2xl',
                'h-[80vh] max-h-[700px] min-h-[450px]',
                // Solid white background for better readability
                'bg-white',
                // Border and shadow
                'border border-gray-200',
                'rounded-2xl',
                'shadow-2xl',
                // Layout
                'flex flex-col',
                'overflow-hidden'
              )}
            >
              {/* Decorative gradient border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-cornflower/20 via-transparent to-brand-purple/20 pointer-events-none" />
              
              {/* Header */}
              <div className="relative border-b border-border/30 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Ava's face (plan 03 CHG-14, D7); her name is the heading beside it. */}
                    <CoworkerAvatar
                      name={persona.name}
                      photo={persona.photo}
                      size={48}
                      alt=''
                      className='rounded-full shadow-lg shadow-brand-purple/25'
                    />
                    
                    <div>
                      <h2 
                        id="ai-manager-title"
                        className="text-xl font-semibold text-brand-navy"
                      >
                        {persona.name}
                      </h2>
                      <p 
                        id="ai-manager-description"
                        className="text-sm text-muted-foreground"
                      >
                        {persona.roleLabel}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {chatHistory.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearHistory}
                        className="text-muted-foreground hover:text-foreground gap-1.5"
                        title="Clear conversation"
                      >
                        <Icons.trash className="h-4 w-4" strokeWidth={1.5} />
                        <span className="hidden sm:inline">Clear</span>
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={closeManager}
                      className="text-muted-foreground hover:text-foreground hover:bg-red-50 hover:text-red-600"
                      title="Close (Esc)"
                    >
                      <Icons.close className="h-5 w-5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>

                {/* Page Context Badge */}
                <div className="mt-4 flex items-center gap-3">
                  <div className={cn(
                    'inline-flex items-center gap-2 rounded-full px-3 py-1.5',
                    'bg-gradient-to-r from-brand-cornflower/10 to-brand-purple/10',
                    'text-brand-navy border border-brand-cornflower/20',
                    'text-sm font-medium'
                  )}>
                    <Icons.globe className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
                    <span>Viewing: {getPageName()}</span>
                  </div>
                  
                  <kbd className={cn(
                    'hidden items-center rounded-md px-2 py-1 sm:inline-flex',
                    'text-xs font-medium',
                    'border border-border/50 bg-muted/50 text-muted-foreground'
                  )}>
                    <Icons.command className="h-3 w-3 mr-1" />
                    J to toggle
                  </kbd>
                </div>
              </div>

              {/* Messages Area */}
              <div className="relative flex-1 overflow-y-auto px-6 py-5">
                {chatHistory.length === 0 ? (
                  /* Empty State */
                  <div className="flex h-full flex-col items-center justify-center text-center py-8">
                    <div className={cn(
                      'mb-6 flex h-20 w-20 items-center justify-center rounded-2xl',
                      'bg-gradient-to-br from-brand-cornflower/20 to-brand-purple/20',
                      'ring-1 ring-brand-cornflower/30'
                    )}>
                      <Icons.messageSquare className="h-10 w-10 text-brand-cornflower" strokeWidth={1.5} />
                    </div>
                    
                    <h3 className="font-display text-xl font-semibold text-brand-navy">
                      What do you need?
                    </h3>
                    <p className="mt-2 max-w-sm text-muted-foreground">
                      Ask me anything about your data, or use a quick action below.
                    </p>

                    {/* Quick Actions - Capability Bubbles */}
                    <div className="mt-8 w-full max-w-lg">
                      <CapabilityBubbles prompts={briefing?.suggested_prompts ?? []} onSelect={handleQuickAction} />
                    </div>
                  </div>
                ) : (
                  /* Chat Messages */
                  <div className="space-y-4">
                    {chatHistory
                      // An answer aborted before anything arrived has nothing to show.
                      .filter((m) => m.role === 'user' || m.isLoading || m.content || (m.steps?.length ?? 0) > 0)
                      .map((message) => (
                        <ChatMessage
                          key={message.id}
                          message={message}
                          userName={session?.user?.name || undefined}
                          userImage={session?.user?.image}
                          assistantName={persona.name}
                          assistantPhoto={persona.photo}
                          onRetry={handleSendMessage}
                        />
                      ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="relative border-t border-border/30">
                {/* Stays enabled while Ava answers: a new question aborts the old answer (D18). */}
                <ChatInput
                  onSend={handleSendMessage}
                  placeholder={isTyping ? `${persona.name} is answering. Ask something else...` : `Ask ${persona.name}...`}
                />
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
